import React, {useRef, useEffect, useState} from 'react';
import {useMonaco} from '@monaco-editor/react';
import MonacoEditor from '@monaco-editor/react';
import useIDEStore, {initializeStore} from './zustand/useIDEStore';
import {editor} from 'monaco-editor';
import {CanisterInfo} from '../types';
import {NPMPackageInfo, PackageInfo, Project} from '../UserCanister/candid/main.did';
import {Principal} from '@dfinity/principal';
import {useAuth} from '../../AuthProvider';
import Sidebar from './Sidebar';
import MotokoPackagesDialog from './MotokoPackagesDialog';
import NPMPackagesDialog from './NPMPackagesDialog';
import CompilerErrorDialog from './CompilerErrorDialog';
import {getMotokoSidebarPackages, getNPMSidebarPackages, getPackages} from './packageUtils';
import TabbedConsole from "./TabbedConsole";
import * as Monaco from 'monaco-editor';
import {pathUtils} from "./file/pathUtils";
import VersionToolbar from "./project/VersionToolbar";
import VersioningPanel from "./project/VersionPanel";
import CreateVersionDialog from "./project/CreateVersionDialog";
import ClassInitArgsDialog from "./ClassInitArgsDialog";
import DeployButton from "./DeployButton";
import ChatSidebar from './ChatSidebar';
import AIStudioView from "./AIStudio/AIStudioView";
import JSZip from "jszip";


interface MonacoIDEWithZustandProps {
    project: Project;
    canisterId: string;
    onError: (error: string | null) => void;
    onSuccess: (message: string | null) => void;
    userCanisters: CanisterInfo[];
    onProjectUpdate?: () => Promise<void>;
    userPrincipal: Principal | null;
    onAddCanisterToProject?: (
        projectId: string,
        canisterId: string,
        onSuccess?: (message: string) => void,
        onError?: (message: string) => void
    ) => Promise<boolean>;
    onRemoveCanisterFromProject?: (
        projectId: string,
        canisterId: Principal,
        onSuccess?: (message: string) => void,
        onError?: (message: string) => void
    ) => Promise<boolean>;
}

const defaultProject: Project = {
    id: 'none',
    name: 'No Project Selected',
    description: undefined,
    projectType: {
        name: 'None',
        subType: 'None',
    },
    canisters: [],
    motokoPackages: [] as [] | [Array<PackageInfo>],
    npmPackages: [] as [] | [Array<NPMPackageInfo>],
    created: BigInt(0),
    updated: BigInt(0),
    visibility: 'private',
    status: 'active',
    collaborators: undefined,
    templateId: undefined,
    workingCopyBaseVersion: []
};


export interface ProjectFile {
    name: string;    // Includes full path, e.g., "src/main.mo"
    content: string;
    fullId?: string;
    path?: string;
    fileName?: string;
    id?: string;
    // Optional extensions for dual file system support
    fileSystem?: 'backend' | 'frontend' | 'asset';
    assetKey?: string;
    assetUrl?: string;
    displayIcon?: string;
    displayColor?: string;
    tooltipSuffix?: string;
    mimeType?: string;
    size?: number;
}

interface FileTreeNode {
    name: string;
    path: string;
    isDirectory: boolean;
    children?: FileTreeNode[];
    originalFile?: ProjectFile;
    containsMixedFileSystems?: boolean;
    fileSystemType?: 'backend' | 'frontend' | 'asset';
}

const MonacoIDEWithZustand: React.FC<MonacoIDEWithZustandProps> = ({
                                                                       project,
                                                                       canisterId,
                                                                       onError,
                                                                       onSuccess,
                                                                       userCanisters,
                                                                       onProjectUpdate,
                                                                       userPrincipal,
                                                                       onAddCanisterToProject,
                                                                       onRemoveCanisterFromProject,
                                                                   }) => {
        // Get authentication context
        const {systemCanisters, identity} = useAuth();

        // Get Monaco instance
        const monaco = useMonaco();

        // Editor reference
        const editorRef = useRef<editor.IStandaloneCodeEditor>();

        // Get state from store (selective subscriptions for performance)
        const activeFile = useIDEStore(state => state.activeFile);
        const fileContent = useIDEStore(state => state.fileContent);
        const hasUnsavedChanges = useIDEStore(state => state.hasUnsavedChanges);
        const compilationStatus = useIDEStore(state => state.compilationStatus);
        const activeType = useIDEStore(state => state.activeType);
        const fileTree = useIDEStore(state => state.fileTree);
        const compilerReady = useIDEStore(state => state.compilerReady);
        const files = useIDEStore(state => state.files);
        const loading = useIDEStore(state => state.loading);
        const isDownloadingFiles = useIDEStore(state => state.isDownloadingFiles);
        const isMotokoPackagesDialogOpen = useIDEStore(state => state.isMotokoPackagesDialogOpen);
        const isNPMPackagesDialogOpen = useIDEStore(state => state.isNPMPackagesDialogOpen);
        const showNewFileDialog = useIDEStore(state => state.showNewFileDialog);
        const showNewFolderDialog = useIDEStore(state => state.showNewFolderDialog);
        const showDeleteConfirmation = useIDEStore(state => state.showDeleteConfirmation);
        const showSaveConfirmation = useIDEStore(state => state.showSaveConfirmation);
        const showWasmNotFoundDialog = useIDEStore(state => state.showWasmNotFoundDialog);
        const showCompilerErrors = useIDEStore(state => state.showCompilerErrors);
        const compilerDiagnostics = useIDEStore(state => state.compilerDiagnostics);
        const deploymentError = useIDEStore(state => state.deploymentError);
        const fileToDelete = useIDEStore(state => state.fileToDelete);
        const newFileName = useIDEStore(state => state.newFileName);
        const newFilePath = useIDEStore(state => state.newFilePath);
        const useCustomPath = useIDEStore(state => state.useCustomPath);

        // Get actions from store
        const handleEditorChange = useIDEStore(state => state.handleEditorChange);
        const setActiveType = useIDEStore(state => state.setActiveType);
        const toggleClaudePanel = useIDEStore(state => state.toggleClaudePanel);
        const toggleMotokoPackagesDialog = useIDEStore(state => state.toggleMotokoPackagesDialog);
        const toggleNPMPackagesDialog = useIDEStore(state => state.toggleNPMPackagesDialog);
        const setActiveFile = useIDEStore(state => state.setActiveFile);
        const handleClaudeContentUpdate = useIDEStore(state => state.handleClaudeContentUpdate);
        const showNewFile = useIDEStore(state => state.showNewFile);
        const showNewFolder = useIDEStore(state => state.showNewFolder);
        const deleteFile = useIDEStore(state => state.deleteFile);
        const confirmDeleteFile = useIDEStore(state => state.confirmDeleteFile);
        const saveFile = useIDEStore(state => state.saveFile);
        const createFolder = useIDEStore(state => state.createFolder);
        const deploy = useIDEStore(state => state.deploy);
        const downloadWasm = useIDEStore(state => state.downloadWasm);
        const addMotokoPackage = useIDEStore(state => state.addMotokoPackage);
        const removeMotokoPackage = useIDEStore(state => state.removeMotokoPackage);
        const addNPMPackage = useIDEStore(state => state.addNPMPackage);
        const removeNPMPackage = useIDEStore(state => state.removeNPMPackage);

        const isVersioningDialogOpen = useIDEStore(state => state.isVersioningDialogOpen);
        const toggleVersioningDialog = useIDEStore(state => state.toggleVersioningDialog);
        const currentVersionId = useIDEStore(state => state.currentVersionId);
        const projectVersions = useIDEStore(state => state.projectVersions);
        const switchToVersion = useIDEStore(state => state.switchToVersion);
        const createNewVersion = useIDEStore(state => state.createNewVersion);
        const updateVersionStatus = useIDEStore(state => state.updateVersionStatus);
        const activeTab = useIDEStore(state => state.activeTab);
        const [isCreateVersionDialogOpen, setIsCreateVersionDialogOpen] = useState(false);
        const initializeProjectVersion = useIDEStore(state => state.initializeProjectVersion);
        const storeProject = useIDEStore(state => state.project) || defaultProject;
        const showWasmSelectionDialog = useIDEStore(state => state.showWasmSelectionDialog);
        const availableWasmFiles = useIDEStore(state => state.availableWasmFiles);
        const downloadSelectedWasm = useIDEStore(state => state.downloadSelectedWasm);
        const hideWasmSelection = useIDEStore(state => state.hideWasmSelection);

        const showPayloadSizeErrorDialog = useIDEStore(state => state.showPayloadSizeErrorDialog);
        const payloadSizeErrorMessage = useIDEStore(state => state.payloadSizeErrorMessage);

        const [showClassInitArgsDialog, setShowClassInitArgsDialog] = useState(false);
        const [actorClassInfo, setActorClassInfo] = useState({className: '', initParams: ''});
        const [pendingDeploymentId, setPendingDeploymentId] = useState(null);

        const [installMode, setInstallMode] = useState('upgrade'); // Default to 'upgrade' with EOP
        const [pendingDeploymentMode, setPendingDeploymentMode] = useState(null);
        const [pendingProjectType, setPendingProjectType] = useState('icpstudio'); // 🔥 ADD THIS LINE
        const [showLoggingPreviewDialog, setShowLoggingPreviewDialog] = useState(false);
        const [previewCode, setPreviewCode] = useState('');

        const [assetRefreshTrigger, setAssetRefreshTrigger] = useState<number>(0);
        const [isChatExpanded, setIsChatExpanded] = useState(false);
        const [pendingTargetCanisterId, setPendingTargetCanisterId] = useState<string | null>(null);
        const showWasmDeletionDialog = useIDEStore(state => state.showWasmDeletionDialog);
        const availableWasmFilesForDeletion = useIDEStore(state => state.availableWasmFilesForDeletion);
        const showOversizedWasmWarning = useIDEStore(state => state.showOversizedWasmWarning);
        const oversizedWasmFiles = useIDEStore(state => state.oversizedWasmFiles);

        // Helper function to get language from filename
        const getLanguageFromActiveFile = (fileName: string): string => {
            const extension = fileName.split('.').pop()?.toLowerCase() || '';
            switch (extension) {
                case 'mo':
                    return 'motoko';
                case 'ts':
                case 'tsx':
                    return 'typescript';
                case 'js':
                case 'jsx':
                    return 'javascript';
                case 'css':
                    return 'css';
                case 'html':
                    return 'html';
                case 'json':
                    return 'json';
                default:
                    return 'plaintext';
            }
        };

        const SelectProjectIcon = () => (
            <svg width="400" height="400" viewBox="0 0 24 24" fill="none" stroke="#5C4033" strokeWidth="1.5">
                <path d="M2 6a1 1 0 0 1 1-1h5l2 2h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6z"/>
            </svg>
        );

        const versionPanelInitialized = useRef(false);

        const chatSidebarRef = useRef<any>(null);


        const handleCreateVersionClick = () => {
            setIsCreateVersionDialogOpen(true);
        };

        const handleCreateVersion = (versionData) => {
            createNewVersion(versionData);
        };


        const handleFixDeploymentError = () => {
            if (!deploymentError) return;

            // Send message to ChatSidebar if it's expanded
            if (isChatExpanded && chatSidebarRef.current?.sendFixMessage) {
                chatSidebarRef.current.sendFixMessage(deploymentError);
                // 🆕 NEW: Clear the deployment error since we're now actively fixing it
                console.log('🔧 Editor: Clearing deployment error since AI is now handling it');
                useIDEStore.getState().setDeploymentError(null);
            } else {
                // If chat is collapsed, expand it and then send the message
                setIsChatExpanded(true);
                setTimeout(() => {
                    if (chatSidebarRef.current?.sendFixMessage) {
                        chatSidebarRef.current.sendFixMessage(deploymentError);
                        // 🆕 NEW: Clear the deployment error since we're now actively fixing it
                        console.log('🔧 Editor: Clearing deployment error since AI is now handling it');
                        useIDEStore.getState().setDeploymentError(null);
                    }
                }, 100); // Small delay to allow expansion
            }
        };

        // Helper function to extract all directories from fileTree
        const generateDirectoryOptions = (node: FileTreeNode | null): { name: string, path: string }[] => {
            if (!node) return [];

            const result: { name: string, path: string }[] = [];

            // Add current node if it's a directory
            if (node.isDirectory) {
                result.push({
                    name: node.name === '/' ? 'Root' : node.name,
                    path: node.path
                });
            }

            // Recursively add child directories
            if (node.children) {
                for (const child of node.children) {
                    if (child.isDirectory) {
                        const childDirs = generateDirectoryOptions(child);
                        result.push(...childDirs);
                    }
                }
            }

            return result;
        };

        useEffect(() => {
            if (project && project.id !== 'none' && userPrincipal && systemCanisters.userActor) {
                // Only run if project is not null and valid
                useIDEStore.getState().getProjectVersions();
            }
        }, [project?.id, userPrincipal, systemCanisters.userActor]);

        // Initialize the store with dependencies
        useEffect(() => {
            initializeStore({
                userActor: systemCanisters.userActor,
                platformActor: systemCanisters.platformActor,
                identity,
                userPrincipal,
                userCanisters,
                onError,
                onSuccess,
                onProjectUpdate,
                activeTab: 'ai-studio'
            });
            // We only want to initialize once, so we don't include all dependencies in the array
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        // Set project in store when it changes
        useEffect(() => {
            if (project && project.id) {
                // Get the current project from the store
                const currentProject = useIDEStore.getState().project;

                // Only do a full reset if the project ID has changed
                if (!currentProject || currentProject.id !== project.id) {
                    console.log('[IDE] Project ID changed, checking if this is user-initiated');

                    // Check if this is likely a user-initiated change by looking at the call stack
                    const stack = new Error().stack || '';
                    const isUserInitiated = stack.includes('onChange') ||
                        stack.includes('onClick') ||
                        stack.includes('handleCreateProject') ||
                        stack.includes('handleCreateAIProject');

                    if (isUserInitiated) {
                        console.log('[IDE] Marking as user-initiated project switch');
                        useIDEStore.getState().markUserInitiatedProjectSwitch(project.id);
                    }

                    console.log('[IDE] Project ID changed, doing full reset');
                    useIDEStore.getState().setProject(project);
                } else {
                    // Otherwise, just update the project without resetting
                    useIDEStore.setState({
                        project: project
                    });
                    console.log('[IDE] Project updated without reset');
                }
            }
        }, [project]);


        // Initialize Monaco and compiler when ready
        useEffect(() => {
            if (monaco) {
                // Setup early debugging and marker handling
                console.log('[MonacoIDE] Monaco instance available, setting up early marker handling');

                // This function will be called later in EditorModelManager but it's important
                // to register it as early as possible
                const registerEarlyMarkerFilter = () => {
                    const originalSetModelMarkers = monaco.editor.setModelMarkers;
                    monaco.editor.setModelMarkers = (model, owner, markers) => {
                        // Log all marker operations
                        // console.log(`[MARKERS] ${markers.length} markers with owner "${owner}" being set on ${model.uri.toString()}`);

                        // Filter out problematic markers
                        const filteredMarkers = markers.filter(marker => {
                            if (marker.code === '2347' || (typeof marker.code === 'number' && marker.code === 2347)) {
                                return false;
                            }
                            return true;
                        });

                        return originalSetModelMarkers.call(monaco.editor, model, owner, filteredMarkers);
                    };
                    console.log('[MonacoIDE] Early marker filter registered');
                };

                // Call immediately
                registerEarlyMarkerFilter();

                // Let the store run its normal initialization
                useIDEStore.getState().initializeMonaco(monaco);
                useIDEStore.getState().initializeCompiler();
            }
        }, [monaco]);

        // Set editor reference when available
        useEffect(() => {
            if (editorRef.current) {
                useIDEStore.getState().setEditorRef(editorRef);
            }
        }, [editorRef.current]);

        useEffect(() => {
            if (project && project.id) {
                const currentProject = useIDEStore.getState().project;

                if (!currentProject || currentProject.id !== project.id) {
                    console.log('[IDE] Project ID changed, initiating coordinated switch');
                    (async () => {
                        try {
                            await useIDEStore.getState().setProject(project);
                            console.log('[IDE] Project switch completed successfully');
                        } catch (error) {
                            console.error('[IDE] Error during project switch:', error);
                        }
                    })();
                }
            }
        }, [project]);


        useEffect(() => {
            console.log('[DEBUG] NPM useEffect triggered:', {
                hasMonaco: !!monaco,
                npmPackages: project.npmPackages,
                npmPackagesLength: project.npmPackages?.[0]?.length,
                projectId: project.id,
                projectName: project.name,
                hasRunInitial: useIDEStore.getState().hasRunInitialPackageUpdate,
                isForced: useIDEStore.getState().isPackageUpdateForced
            });

            if (monaco && project.npmPackages?.[0] && project.id !== 'none' && project.name) {
                // 🔒 PROTECTION: Capture project ID before async operation
                const currentProjectId = project.id;

                console.log('[DEBUG] Calling updatePackageFile for project:', currentProjectId);

                // Debounce to prevent rapid successive calls
                const timeoutId = setTimeout(() => {
                    const storeProject = useIDEStore.getState().project;
                    if (storeProject?.id === currentProjectId) {
                        console.log('[DEBUG] Project still matches, running updatePackageFile');
                        useIDEStore.getState().updatePackageFile();
                    } else {
                        console.log(`[DEBUG] Project changed during NPM useEffect delay: ${currentProjectId} → ${storeProject?.id}, skipping update`);
                    }
                }, 500); // Increased delay to debounce rapid changes

                return () => clearTimeout(timeoutId);
            }
        }, [monaco, project.npmPackages?.[0], project.id, project.name]);


        useEffect(() => {
            console.log('[DEBUG] Motoko useEffect triggered:', {
                hasMonaco: !!monaco,
                motokoPackages: project.motokoPackages,
                motokoPackagesLength: project.motokoPackages?.length,
                projectId: project.id,
                projectName: project.name
            });

            if (monaco && project.motokoPackages && project.id !== 'none' && project.name) {
                // 🔒 PROTECTION: Capture project ID before async operation
                const currentProjectId = project.id;

                console.log('[DEBUG] Calling updateMopsTomlFile for project:', currentProjectId);

                // Check if we're still on the same project before running
                setTimeout(() => {
                    const storeProject = useIDEStore.getState().project;
                    if (storeProject?.id === currentProjectId) {
                        console.log('[DEBUG] Project still matches, running updateMopsTomlFile');
                        useIDEStore.getState().updateMopsTomlFile();
                    } else {
                        console.log(`[DEBUG] Project changed during Motoko useEffect delay: ${currentProjectId} → ${storeProject?.id}, skipping update`);
                    }
                }, 100); // Small delay to let any project switches settle
            }
        }, [monaco, project.motokoPackages, project.id, project.name]);


        // Set up editor model when active file changes
        useEffect(() => {
            if (!monaco || !editorRef.current || !activeFile) return;

            const editorManager = useIDEStore.getState().editorManager;
            if (!editorManager) return;

            try {
                const model = editorManager.setModel(
                    `file:///${activeFile}`,
                    fileContent,
                    getLanguageFromActiveFile(activeFile)
                );
                editorRef.current.setModel(model);

                // Apply theme
                monaco.editor.setTheme('customTheme');
            } catch (error) {
                console.error('[DEBUG] Error setting up model:', error);
            }
        }, [activeFile, monaco, fileContent]);

        // Set up language service when all dependencies are ready
        useEffect(() => {
            const languageService = useIDEStore.getState().languageService;
            const compilerService = useIDEStore.getState().compilerService;
            const editorManager = useIDEStore.getState().editorManager;

            if (editorManager && languageService && compilerService) {
                editorManager.setServices(languageService, compilerService);
            }
        }, []);

        // Add diagnostics handling
        useEffect(() => {
            if (!monaco || !editorRef.current || !compilerReady || !activeFile) {
                return;
            }

            const editor = editorRef.current;
            let debounceTimeout: NodeJS.Timeout | null = null;

            // Function to safely run diagnostics
            const runDiagnostics = () => {
                try {
                    const editor = editorRef.current;
                    if (!editor) return;

                    // Get the current active file from state
                    const state = useIDEStore.getState();
                    const currentFile = state.activeFile;

                    if (!currentFile) {
                        console.warn('[WARN] Cannot run diagnostics: No active file');
                        return;
                    }

                    const content = editor.getValue();

                    // Get the language service from the store
                    const languageService = state.languageService;
                    if (!languageService) {
                        console.warn('[WARN] Language service not available for diagnostics');
                        return;
                    }

                    // Only pass the two parameters that handleDiagnostics accepts
                    languageService.handleDiagnostics(currentFile, content);
                } catch (error) {
                    console.error('[ERROR] Failed to run diagnostics:', error);
                }
            };

            // Debounced diagnostics function
            const debouncedRunDiagnostics = () => {
                if (debounceTimeout) clearTimeout(debounceTimeout);
                debounceTimeout = setTimeout(() => {
                    runDiagnostics();
                }, 300);
            };

            // Initial diagnostics when dependencies are ready
            runDiagnostics();

            // Listener for content changes
            const changeListener = editor.onDidChangeModelContent(() => {
                console.log('[DEBUG] Editor content changed. Running debounced diagnostics...');
                debouncedRunDiagnostics();
            });

            // Cleanup listener and timeout
            return () => {
                changeListener.dispose();
                if (debounceTimeout) clearTimeout(debounceTimeout);
            };
        }, [monaco, compilerReady, activeFile, editorRef.current]);

        // Add a new useEffect to handle active type changes
        useEffect(() => {
            // Only run this logic if we already have files loaded
            if (files.length === 0 || !project || project.id === 'none') return;

            const validFiles = files.filter(file => !file.name.includes('.keep'));
            if (validFiles.length === 0) return;

            // Store the previous active type if it doesn't exist yet
            const previousActiveType = useIDEStore.getState().previousActiveType;
            if (previousActiveType === undefined) {
                useIDEStore.setState({previousActiveType: activeType});
                return;
            }

            // Check if this is an explicit type switch
            const isExplicitTypeSwitch = previousActiveType !== activeType;

            // If the user is editing a file with unsaved changes, don't switch automatically
            const currentState = useIDEStore.getState();
            if (currentState.hasUnsavedChanges && !isExplicitTypeSwitch) {
                console.log('[DEBUG] Not switching default file because there are unsaved changes');
                // Just update the previous type and exit
                useIDEStore.setState({previousActiveType: activeType});
                return;
            }

            // Find appropriate file based on activeType
            if (activeType === 'backend') {
                // Look for main.mo in the backend directory
                const mainMoPath = `${project.name}/src/backend/main.mo`;
                const mainMoFile = validFiles.find(file => file.name === mainMoPath);

                if (mainMoFile) {
                    // Only switch if this is an explicit type switch or we don't have an active file
                    if (isExplicitTypeSwitch || !activeFile) {
                        // Set a flag to indicate this is a user-initiated switch
                        useIDEStore.setState({isUserInitiatedFileSwitch: true});
                        setActiveFile(mainMoFile.name);
                    }
                } else {
                    // Fallback to any .mo file if main.mo isn't found
                    const moFiles = validFiles.filter(file =>
                        file.name.endsWith('.mo') &&
                        file.name.includes('/backend/')
                    );
                    if (moFiles.length > 0 && (isExplicitTypeSwitch || !activeFile)) {
                        useIDEStore.setState({isUserInitiatedFileSwitch: true});
                        setActiveFile(moFiles[0].name);
                    }
                }
            } else { // Frontend
                // Look for App.tsx in the frontend directory
                const appTsxPath = `${project.name}/src/frontend/src/App.tsx`;
                const appTsxFile = validFiles.find(file => file.name === appTsxPath);

                if (appTsxFile) {
                    // Only switch if this is an explicit type switch or we don't have an active file
                    if (isExplicitTypeSwitch || !activeFile) {
                        useIDEStore.setState({isUserInitiatedFileSwitch: true});
                        setActiveFile(appTsxFile.name);
                    }
                } else {
                    // Fallback to any frontend file if App.tsx isn't found
                    const frontendFiles = validFiles.filter(file =>
                        file.name.includes('/frontend/') &&
                        (file.name.endsWith('.tsx') || file.name.endsWith('.ts') ||
                            file.name.endsWith('.jsx') || file.name.endsWith('.js'))
                    );
                    if (frontendFiles.length > 0 && (isExplicitTypeSwitch || !activeFile)) {
                        useIDEStore.setState({isUserInitiatedFileSwitch: true});
                        setActiveFile(frontendFiles[0].name);
                    }
                }
            }

            // Update previous active type after processing
            useIDEStore.setState({previousActiveType: activeType});
        }, [activeType, files, project, activeFile]);
        //Update model language after content change
        useEffect(() => {
            if (monaco && editorRef.current && activeFile) {
                const model = editorRef.current.getModel();
                if (model) {
                    const extension = activeFile.split('.').pop()?.toLowerCase();
                    let language;

                    switch (extension) {
                        case 'mo':
                            language = 'motoko';
                            break;
                        case 'ts':
                            language = 'typescript';
                            break;
                        case 'tsx':
                            language = 'typescript';
                            break;
                        case 'js':
                            language = 'javascript';
                            break;
                        case 'jsx':
                            language = 'javascript';
                            break;
                        case 'css':
                            language = 'css';
                            break;
                        case 'html':
                            language = 'html';
                            break;
                        case 'json':
                            language = 'json';
                            break;
                        default:
                            language = 'plaintext';
                    }

                    monaco.editor.setModelLanguage(model, language);
                }
            }
        }, [monaco, editorRef.current, activeFile, fileContent]);

        //Force theme application
        useEffect(() => {
            if (monaco) {
                // Apply theme multiple times with delays to ensure it's set
                monaco.editor.setTheme('customTheme');

                setTimeout(() => {
                    monaco.editor.setTheme('customTheme');
                }, 200);

                setTimeout(() => {
                    monaco.editor.setTheme('customTheme');
                }, 500);
            }
        }, [monaco, activeFile]);

        useEffect(() => {
            if (project && project.id !== 'none' && userPrincipal && systemCanisters.userActor) {
                // Fetch versions when project loads
                useIDEStore.getState().getProjectVersions();
            }
        }, [project.id, userPrincipal, systemCanisters.userActor]);

        useEffect(() => {
            if (activeTab === 'versions' && currentVersionId && !versionPanelInitialized.current) {
                console.log(`[IDE] Initializing version panel with current version: ${currentVersionId}`);
                versionPanelInitialized.current = true;
            } else if (activeTab !== 'versions') {
                // Reset initialization flag when leaving versions tab
                versionPanelInitialized.current = false;
            }
        }, [activeTab, currentVersionId]);

        useEffect(() => {
            if (monaco) {
                console.log('[CRITICAL DEBUG] Installing logging-only marker observer');

                // Store original function
                const originalSetModelMarkers = monaco.editor.setModelMarkers;

                // Replace with our logging-only version that doesn't modify markers
                monaco.editor.setModelMarkers = (model, owner, markers) => {
                    // Log marker operations without filtering
                    // console.log(`[MARKER DEBUG] Setting ${markers.length} markers with owner "${owner}" on ${model.uri.toString()}`);

                    // if (markers.length > 0) {
                    //     // Log the first few markers for debugging
                    //     console.log(`[MARKER SAMPLES] First ${Math.min(3, markers.length)} markers:`,
                    //         markers.slice(0, 3).map(m => ({
                    //             severity: m.severity,
                    //             message: m.message,
                    //             code: m.code,
                    //             startLineNumber: m.startLineNumber,
                    //             source: m.source
                    //         }))
                    //     );
                    // }

                    // Call original function with the unmodified markers
                    return originalSetModelMarkers.call(monaco.editor, model, owner, markers);
                };

                console.log('[CRITICAL DEBUG] Successfully installed marker observer');
            }
        }, [monaco]);

        useEffect(() => {
            console.log(`[DEBUG] File tree updated. Project: ${project?.id}, Files: ${files?.length}, Tree nodes: ${fileTree?.children?.length}`);

            if (fileTree && files.length > 0) {
                // Log first few file paths to identify which project they belong to
                const sampleFiles = files.slice(0, 3).map(f => f.name);
                console.log(`[DEBUG] Sample files:`, sampleFiles);

                // Check if files match current project
                const projectMatches = files.some(f => f.name.includes(project?.name || ''));
                if (!projectMatches && project?.name) {
                    console.warn(`[DEBUG] WARNING: Files don't seem to match current project ${project.name}`);
                }
            }
        }, [fileTree, files, project?.id, project?.name]);

        // Add this useEffect to generate preview when dialog opens:
        useEffect(() => {
            if (showLoggingPreviewDialog && activeFile && fileContent) {
                const modifiedCode = injectLoggingSupport(fileContent, activeFile);
                setPreviewCode(modifiedCode);
            }
        }, [showLoggingPreviewDialog, activeFile, fileContent]);


        useEffect(() => {
            console.log('showLoggingPreviewDialog changed to:', showLoggingPreviewDialog);
        }, [showLoggingPreviewDialog]);


        // Handle editor mount
        const handleEditorDidMount = async (editor: editor.IStandaloneCodeEditor) => {
            console.log('[DEBUG] Editor mount starting');
            editorRef.current = editor;


            // If Monaco isn't available yet, we can't proceed
            if (!monaco) return;

            if (monaco && monaco.languages.typescript) {
                monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
                    diagnosticCodesToIgnore: [2347]
                });
                monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
                    diagnosticCodesToIgnore: [2347]
                });
            }

            if (monaco) {
                // Ensure all languages are registered
                const languagesToRegister = [
                    {id: 'motoko', extensions: ['.mo']},
                    {id: 'typescript', extensions: ['.ts', '.tsx']},
                    {id: 'javascript', extensions: ['.js', '.jsx']},
                    {id: 'css', extensions: ['.css']},
                    {id: 'html', extensions: ['.html']},
                    {id: 'json', extensions: ['.json']}
                ];

                languagesToRegister.forEach(lang => {
                    if (!monaco.languages.getLanguages().some(l => l.id === lang.id)) {
                        monaco.languages.register(lang);
                    }
                });

                // Force set the language based on activeFile
                const model = editor.getModel();
                if (model && activeFile) {
                    const extension = activeFile.split('.').pop()?.toLowerCase();
                    if (extension) {
                        const language = getLanguageFromActiveFile(activeFile);
                        monaco.editor.setModelLanguage(model, language);
                    }
                }
            }

            // Get the editor manager from the store
            const editorManager = useIDEStore.getState().editorManager;
            if (!editorManager) return;

            try {
                // Add key commands
                editor.addCommand(
                    monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyC,
                    () => {
                        const selection = editor.getSelection();
                        if (selection) {
                            editor.trigger('keyboard', 'editor.action.commentLine', null);
                        }
                    }
                );

                editor.addCommand(
                    monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyD,
                    () => {
                        const selection = editor.getSelection();
                        if (selection) {
                            editor.executeEdits('delete-line', [{
                                range: new monaco.Range(
                                    selection.startLineNumber,
                                    1,
                                    selection.startLineNumber + 1,
                                    1
                                ),
                                text: ''
                            }]);
                            // Maintain cursor position
                            editor.setPosition({
                                lineNumber: selection.startLineNumber,
                                column: 1
                            });
                        }
                    }
                );

                editor.addCommand(
                    monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
                    () => {
                        // Show the save confirmation dialog
                        useIDEStore.setState({showSaveConfirmation: true});

                        // Return true to indicate the command was handled and prevent browser's default save
                        return true;
                    }
                );

                editor.addCommand(
                    monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyR,
                    () => {
                        // Focus the file search input in the sidebar
                        const searchInput = document.querySelector('input[placeholder*="Search files"]') as HTMLInputElement;
                        if (searchInput) {
                            searchInput.focus();
                            searchInput.select();
                        }
                        return true;
                    }
                );

                // Initialize theme directly here instead of in a useEffect
                console.log('[DEBUG] Initializing theme');
                await editorManager.initializeTheme();
                console.log('[DEBUG] Theme initialized');

                // Add custom styles after theme is ready
                editorManager.addCustomStyles();

                // Register languages
                editorManager.registerLanguages([
                    {id: 'motoko', extensions: ['.mo']},
                    {id: 'typescript', extensions: ['.ts', '.tsx']},
                    {id: 'javascript', extensions: ['.js', '.jsx']},
                    {id: 'css', extensions: ['.css']},
                    {id: 'html', extensions: ['.html']},
                    {id: 'json', extensions: ['.json']}
                ]);

                // Configure TypeScript
                const compilerOptions: Monaco.languages.typescript.CompilerOptions = {
                    target: monaco.languages.typescript.ScriptTarget.ES2015,
                    allowNonTsExtensions: true,
                    moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
                    module: monaco.languages.typescript.ModuleKind.CommonJS,
                    noEmit: true,
                    esModuleInterop: true,
                    jsx: monaco.languages.typescript.JsxEmit.React,
                    reactNamespace: 'React',
                    allowJs: true,
                    typeRoots: ["node_modules/@types"]
                };
                editorManager.configureTypeScript(compilerOptions);

                // Set editor options
                editor.updateOptions({
                    lineNumbers: 'on',
                    renderLineHighlight: 'line',
                    glyphMargin: true,
                    folding: true,
                    lineDecorationsWidth: 0,
                    lineNumbersMinChars: 3,
                    scrollBeyondLastLine: false,
                    minimap: {enabled: false},
                    renderWhitespace: "none",
                    matchBrackets: "always",
                    bracketPairColorization: {
                        enabled: true,
                        independentColorPoolPerBracketType: true,
                    },
                    guides: {
                        bracketPairs: true,
                        bracketPairsHorizontal: true,
                        highlightActiveBracketPair: true,
                        indentation: true
                    },
                    autoClosingBrackets: "always",
                    autoClosingQuotes: "always",
                    hover: {
                        enabled: true,
                        delay: 100,
                        sticky: false,
                        above: false,
                    },
                    fixedOverflowWidgets: true,
                    contextmenu: true,
                    mouseWheelZoom: false,
                    scrollbar: {
                        useShadows: false,
                        vertical: 'visible',
                        horizontal: 'visible',
                        verticalHasArrows: false,
                        horizontalHasArrows: false,
                        horizontalScrollbarSize: 12,
                        verticalScrollbarSize: 12
                    }
                });

                // Set up model if we have an active file
                if (activeFile) {
                    const model = editorManager.setModel(
                        `file:///${activeFile}`,
                        fileContent,
                        getLanguageFromActiveFile(activeFile)
                    );
                    editor.setModel(model);
                }

                // Set content if we have it
                if (fileContent) {
                    editor.setValue(fileContent);
                }

                // Initialize services
                const languageService = useIDEStore.getState().languageService;
                const compilerService = useIDEStore.getState().compilerService;
                if (languageService && compilerService) {
                    editorManager.setServices(languageService, compilerService);
                }

                // Force theme application after everything is set up
                monaco.editor.setTheme('customTheme');

            } catch (error) {
                console.error('[DEBUG] Error in editor mount:', error);
            }
        };

        const handleDeployWithClassCheck = (deploymentId = null, installMode = 'reinstall', projectType = 'icpstudio', targetCanisterId = null, selectedActorFile = null) => {
            console.log("handleDeployWithClassCheck called with:");
            console.log("  - deploymentId:", deploymentId);
            console.log("  - installMode:", installMode);
            console.log("  - projectType:", projectType);
            console.log("  - targetCanisterId:", targetCanisterId);
            console.log("  - selectedActorFile:", selectedActorFile);
            console.log("  - activeFile:", activeFile);

            // Use selectedActorFile if provided, otherwise fall back to activeFile
            const targetFile = selectedActorFile || activeFile;

            // Check if the target file is main.mo
            const isMainMo = targetFile &&
                (targetFile.endsWith('/main.mo') || targetFile.endsWith('/Main.mo'));

            if (!isMainMo) {
                deploy(deploymentId, null, installMode, projectType, targetCanisterId, targetFile);
                return;
            }

            // Get the content of the file
            const content = fileContent;

            // Check if it's a class-based actor
            const classMatch = content.match(/actor\s+class\s+(\w+)\s*\((.*?)\)/);
            if (classMatch) {
                // Found a class-based actor
                setActorClassInfo({
                    className: classMatch[1],
                    initParams: classMatch[2]
                });
                setPendingDeploymentId(deploymentId);
                setPendingDeploymentMode(installMode);
                setPendingProjectType(projectType);
                setPendingTargetCanisterId(targetCanisterId);
                setShowClassInitArgsDialog(true);
            } else {
                deploy(deploymentId, null, installMode, projectType, targetCanisterId, targetFile);
            }
        };

        const handleClassInitArgsSubmit = (initArgs) => {
            console.log("Class init args:", initArgs);
            console.log("Pending target canister ID:", pendingTargetCanisterId);

            setShowClassInitArgsDialog(false);

            // FIXED: Pass the stored target canister ID as 5th parameter
            deploy(
                pendingDeploymentId,
                initArgs,
                pendingDeploymentMode,
                pendingProjectType,
                pendingTargetCanisterId
            );

            // Clear all pending state
            setPendingDeploymentId(null);
            setPendingDeploymentMode(null);
            setPendingProjectType('icpstudio');
            setPendingTargetCanisterId(null); // FIXED: Clear the stored canister ID
        };

        const isDeployableFile = (activeFile, fileContent) => {

            return true;

            if (!activeFile) return true;

            // Check if the file is App.tsx - always deployable
            if (activeFile.endsWith('/App.tsx')) {
                return true;
            }

            // For Motoko files, use stricter patterns and specific file checks
            if (activeFile.endsWith('.mo')) {
                // First, check if it's main.mo or Main.mo (regardless of content)
                if (activeFile.endsWith('/main.mo') || activeFile.endsWith('/Main.mo')) {
                    return true;
                }

                // For other .mo files, check for actual actor definitions more strictly
                // Remove comments first to avoid false positives
                const cleanedContent = fileContent
                    .replace(/\/\/.*$/gm, '') // Remove line comments
                    .replace(/\/\*[\s\S]*?\*\//g, ''); // Remove block comments

                // More precise regex patterns for actual actor declarations
                const actorPatterns = [
                    /^actor\s+\w+\s*(\([^)]*\))?\s*\{/m,      // Named actor at start of line
                    /^actor\s+class\s+\w+\s*(\([^)]*\))?\s*\{/m, // Actor class at start of line
                    /^actor\s*\{/m,                               // Anonymous actor at start of line
                    /export\s+actor\s+\w+\s*\{/m,                // Exported named actor
                    /export\s+actor\s*\{/m                       // Exported anonymous actor
                ];

                return actorPatterns.some(pattern => pattern.test(cleanedContent));
            }

            return false;
        };

        const frontendCanisterId = userCanisters?.find(
            userCanister =>
                project?.canisters?.some(
                    projectCanister =>
                        userCanister.principal.toText() === projectCanister.toText() &&
                        userCanister.canisterType === 'frontend'
                )
        )?.principal || undefined;

        // Render placeholder for empty state
        const renderPlaceholder = () => (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#666',
                backgroundColor: '#fff'
            }}>
                <SelectProjectIcon/>
                <div style={{
                    marginTop: '20px',
                    fontSize: '35px',
                    color: '#1a1a1a'
                }}>
                    Please select a project to begin editing
                </div>
            </div>
        );

        // Show placeholder if no project is selected
        if (project.id === 'none') {
            return renderPlaceholder();
        }

        // Add this function to your MonacoIDEWithZustand component:

        const handleEnableLogging = async () => {
            console.log('handleEnableLogging called');

            if (!activeFile || !activeFile.endsWith('.mo')) {
                onError('Please select a Motoko (.mo) file to enable logging');
                return;
            }

            try {
                // Check if logging is already enabled and warn, but still show preview
                if (fileContent.includes('import L "mo:cn-logger/logger"')) {
                    // Just log it, don't block the preview
                    console.log('Logging already appears to be enabled, but showing preview anyway');
                }

                console.log('Opening preview dialog');
                setShowLoggingPreviewDialog(true);
            } catch (error) {
                console.error('Error in handleEnableLogging:', error);
                onError('Failed to analyze file for logging support');
            }
        };

        // Create this utility function:
        const injectLoggingSupport = (originalCode: string, fileName: string): string => {
            let modifiedCode = originalCode;

            // 1. Add import statement if not present
            if (!modifiedCode.includes('import L "mo:cn-logger/logger"')) {
                // Find the last import or add at the top
                const importRegex = /^import\s+.*$/gm;
                const imports = [...modifiedCode.matchAll(importRegex)];

                const loggingImport = 'import L "mo:cn-logger/logger";';

                if (imports.length > 0) {
                    // Insert after last import
                    const lastImport = imports[imports.length - 1];
                    const insertIndex = lastImport.index! + lastImport[0].length;
                    modifiedCode = modifiedCode.slice(0, insertIndex) +
                        '\n' + loggingImport +
                        modifiedCode.slice(insertIndex);
                } else {
                    // Add at the very beginning
                    modifiedCode = loggingImport + '\n\n' + modifiedCode;
                }
            }

            // 2. Add stable logger variable after 'actor {' or 'actor Name {' if not present
            if (!modifiedCode.includes('stableLogger')) {
                // Updated regex to handle both simple actors and named actors like "CryptoPortfolio"
                const actorMatch = modifiedCode.match(/(actor\s+(?:\w+\s+)?(?:class\s+\w+\s*(?:\([^)]*\))?\s*)?\{)/);
                if (actorMatch) {
                    const insertIndex = actorMatch.index! + actorMatch[0].length;
                    const stableVars = `
    // === LOGGING SUPPORT ===
    private stable var stableLogger : ?L.StableLoggerData = null;
    
    private let logger = L.Logger(
        stableLogger,
        ?{
            maxSize = 500;
            retentionDays = 7;
        }
    );
`;
                    modifiedCode = modifiedCode.slice(0, insertIndex) +
                        stableVars +
                        modifiedCode.slice(insertIndex);
                }
            }

            // 3. Add exposed logger methods before the final '}' (only if not already present)
            if (!modifiedCode.includes('getNewLogsSince')) {
                const lastBraceIndex = modifiedCode.lastIndexOf('}');
                if (lastBraceIndex > -1) {
                    const exposedMethods = `
    // === LOGGING METHODS ===
    public func logInfo(message: Text) : async () {
        logger.info(message);
    };

    public func logWarn(message: Text) : async () {
        logger.warn(message);
    };

    public func logError(message: Text) : async () {
        logger.error(message);
    };

    public func logDebug(message: Text) : async () {
        logger.dbg(message);
    };

    public query func getLogs() : async [Text] {
        logger.getLogs()
    };

    public query func getNewLogsSince(marker: Nat, maxLogsOpt: ?Nat) : async {
        logs: [Text];
        nextMarker: Nat;
    } {
        logger.getNewLogsSince(marker, maxLogsOpt)
    };

    public func clearAllLogs() : async Nat {
        logger.clearLogs();
        return 0;
    };

    // === SYSTEM METHODS ===
    system func postupgrade() {
        stableLogger := ?logger.toStable();
    };

`;
                    modifiedCode = modifiedCode.slice(0, lastBraceIndex) +
                        exposedMethods +
                        modifiedCode.slice(lastBraceIndex);
                }
            }

            return modifiedCode;
        };

        // async function deleteWasm(currentVersionId) {
        //     const actor = systemCanisters.userActor;
        //     const versionParam: [] | [string] = currentVersionId ? [currentVersionId] : [];
        //
        //     let totalDeleted = 0;
        //     let errors = [];
        //
        //     console.log("Starting targeted WASM deletion for project:", project.name);
        //
        //     // Based on your backend's finalizeWasmUpload method, large WASM files are stored in "wasms" directory
        //     // The key insight: your backend is storing chunked WASM uploads in the artifact system
        //
        //     const wasmPaths = [
        //         "wasms", // Primary location where finalizeWasmUpload stores large WASM files
        //         `${project.name}/src/backend`, // Sometimes stored here
        //         `src/backend`, // Alternative location
        //         `wasms/${project.name}` // Nested in wasms
        //     ];
        //
        //     // Most likely filenames based on your system
        //     const wasmFilenames = [
        //         "user.wasm", // This is likely the correct name for your system
        //         `${project.name.toLowerCase()}.wasm`, // kontext_kcp.wasm
        //         "main.wasm",
        //         "backend.wasm",
        //         `${project.name}.wasm` // KONTEXT_KCP.wasm
        //     ];
        //
        //     console.log("Trying to delete WASM files from artifact system...");
        //
        //     // Try each combination of path and filename
        //     for (const wasmPath of wasmPaths) {
        //         for (const filename of wasmFilenames) {
        //             try {
        //                 console.log(`Attempting to delete: ${wasmPath}/${filename}`);
        //
        //                 const result = await actor.deleteCodeArtifact(
        //                     userPrincipal,
        //                     project.id,
        //                     wasmPath,
        //                     filename,
        //                     versionParam
        //                 );
        //
        //                 if ('ok' in result) {
        //                     console.log(`✅ Successfully deleted: ${wasmPath}/${filename}`);
        //                     totalDeleted++;
        //                 } else {
        //                     console.log(`❌ Not found: ${wasmPath}/${filename} - ${result.err}`);
        //                 }
        //             } catch (err) {
        //                 console.log(`Error deleting ${wasmPath}/${filename}:`, err);
        //             }
        //         }
        //     }
        //
        //     // If we still haven't found any files, try a more exhaustive search
        //     if (totalDeleted === 0) {
        //         console.log("No WASM files found in common locations. Trying alternative approaches...");
        //
        //         // Try to read the artifact directly to see what's actually stored
        //         try {
        //             console.log("Checking what artifacts exist in the project...");
        //
        //             // Try to list artifacts if such a method exists, or try common variations
        //             const possibleDirectories = ["wasms", "", "artifacts", "binaries"];
        //             const possibleFiles = ["user.wasm", "main.wasm", "backend.wasm"];
        //
        //             for (const dir of possibleDirectories) {
        //                 for (const file of possibleFiles) {
        //                     try {
        //                         // Try to read first to see if it exists
        //                         const readResult = await actor.readCodeArtifact(
        //                             userPrincipal,
        //                             project.id,
        //                             dir,
        //                             file,
        //                             versionParam
        //                         );
        //
        //                         if ('ok' in readResult) {
        //                             console.log(`Found WASM file: ${dir}/${file} - attempting deletion`);
        //
        //                             const deleteResult = await actor.deleteCodeArtifact(
        //                                 userPrincipal,
        //                                 project.id,
        //                                 dir,
        //                                 file,
        //                                 versionParam
        //                             );
        //
        //                             if ('ok' in deleteResult) {
        //                                 console.log(`✅ Successfully deleted: ${dir}/${file}`);
        //                                 totalDeleted++;
        //                             }
        //                         }
        //                     } catch (e) {
        //                         // Ignore - file doesn't exist
        //                     }
        //                 }
        //             }
        //         } catch (error) {
        //             console.error("Error during exhaustive search:", error);
        //         }
        //     }
        //
        //     // Report results
        //     if (totalDeleted > 0) {
        //         const message = `Successfully deleted ${totalDeleted} WASM file(s). The payload size error should be resolved.`;
        //         console.log(message);
        //         onSuccess?.(message);
        //
        //         // Force a project refresh after a delay
        //         setTimeout(() => {
        //             console.log("Triggering project refresh after WASM deletion...");
        //             window.location.reload(); // Force a complete reload to clear any cached state
        //         }, 2000);
        //
        //     } else {
        //         // If we can't find/delete the WASM files, provide guidance
        //         const message = `No WASM files found to delete. The large file might be stored differently. Try redeploying your project to recreate without the large WASM.`;
        //         console.log(message);
        //         onError?.(message);
        //
        //         // Provide additional debugging info
        //         console.log("Debugging info:");
        //         console.log("- Project ID:", project.id);
        //         console.log("- Version ID:", currentVersionId);
        //         console.log("- The payload error suggests a file ~2.87MB exists");
        //         console.log("- Consider checking your backend's artifact storage system");
        //     }
        // }

        // Replace the existing deleteWasm function with this enhanced version
        const handleDeleteWasmClick = async () => {
            console.log("=== DELETING LARGE FILES TO FREE UP SPACE ===");
            const actor = systemCanisters.userActor;
            const versionParam: [] | [string] = currentVersionId ? [currentVersionId] : [];

            // The 4 largest files to delete
            const filesToDelete = [
                { path: "KONTEXT_KCP/src/frontend/src", fileName: "App.tsx" },
                { path: "KONTEXT_KCP/src/frontend/src/store", fileName: "appStore.ts" },
                { path: "KONTEXT_KCP/src/frontend/src/services", fileName: "UserCanisterService.ts" },
                { path: "KONTEXT_KCP/src/backend/src/user", fileName: "user.mo" }
            ];

            try {
                console.log(`Starting deletion of ${filesToDelete.length} large files...`);
                let deletedCount = 0;
                let failedCount = 0;

                // Delete each file
                for (let i = 0; i < filesToDelete.length; i++) {
                    const file = filesToDelete[i];
                    const progress = Math.round(((i + 1) / filesToDelete.length) * 100);

                    console.log(`[${progress}%] Deleting ${file.path}/${file.fileName}...`);

                    try {
                        const result = await actor.deleteCodeArtifact(
                            userPrincipal,
                            project.id,
                            file.path,
                            file.fileName,
                            versionParam
                        );

                        if ('ok' in result) {
                            console.log(`✅ Successfully deleted: ${file.path}/${file.fileName}`);
                            deletedCount++;
                        } else {
                            console.log(`❌ Failed to delete ${file.fileName}: ${result.err}`);
                            failedCount++;
                        }
                    } catch (error) {
                        console.log(`❌ Error deleting ${file.fileName}:`, error);
                        failedCount++;
                    }

                    // Add a small delay between deletions to avoid overwhelming the canister
                    if (i < filesToDelete.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 500));
                    }
                }

                console.log(`\nDeletion complete: ${deletedCount} files deleted, ${failedCount} failed`);

                if (deletedCount > 0) {
                    onSuccess(`Successfully deleted ${deletedCount} large files${failedCount > 0 ? ` (${failedCount} files failed)` : ''}. Project should now be under 2MB limit.`);

                    // Refresh the project files after deletion
                    setTimeout(() => {
                        if (useIDEStore.getState().refreshFiles) {
                            useIDEStore.getState().refreshFiles();
                        }
                    }, 1000);
                } else {
                    onError(`No files were deleted. ${failedCount} deletion attempts failed.`);
                }

            } catch (error) {
                console.log("Error during deletion process:", error);
                onError(`Deletion failed: ${error instanceof Error ? error.message : String(error)}`);
            }
        };

        const handleDeleteSelectedWasm = async (wasmFile: { path: string, fileName: string, fullPath: string }) => {
            const actor = systemCanisters.userActor;
            const versionParam: [] | [string] = currentVersionId ? [currentVersionId] : [];

            try {
                console.log(`Deleting WASM file: ${wasmFile.fullPath}`);

                const result = await actor.deleteCodeArtifact(
                    userPrincipal,
                    project.id,
                    wasmFile.path,
                    wasmFile.fileName,
                    versionParam
                );

                if ('ok' in result) {
                    onSuccess(`Successfully deleted ${wasmFile.fileName} (${wasmFile.fullPath})`);

                    // Close dialog and refresh
                    useIDEStore.setState({
                        showWasmDeletionDialog: false,
                        availableWasmFilesForDeletion: []
                    });

                    // Refresh project files
                    setTimeout(() => {
                        useIDEStore.getState().refreshFiles();
                    }, 1000);
                } else {
                    onError(`Failed to delete ${wasmFile.fileName}: ${result.err}`);
                }
            } catch (error) {
                console.error("Error deleting WASM file:", error);
                onError(`Error deleting ${wasmFile.fileName}: ${error instanceof Error ? error.message : String(error)}`);
            }
        };


        const WasmSelectionDialog = ({
                                         isOpen,
                                         wasmFiles,
                                         onDownload,
                                         onClose
                                     }: {
            isOpen: boolean;
            wasmFiles: Array<{
                path: string;
                fileName: string;
                size?: number;
                lastModified?: string;
                actorName?: string;
            }>;
            onDownload: (wasmFile: any) => void;
            onClose: () => void;
        }) => {
            if (!isOpen) return null;

            const formatFileSize = (bytes: number) => {
                if (bytes === 0) return '0 Bytes';
                const k = 1024;
                const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
            };

            return (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: "#fdf6e3",
                        borderRadius: "8px",
                        width: "600px",
                        maxWidth: "90%",
                        maxHeight: "80vh",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        boxShadow: "0 4px 20px rgba(139, 69, 19, 0.3)"
                    }}>
                        <div style={{
                            padding: "16px 20px",
                            borderBottom: "1px solid #8B4513",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#f3e5d8"
                        }}>
                            <h3 style={{
                                fontSize: "18px",
                                fontWeight: "bold",
                                color: "#5C4033",
                                margin: 0
                            }}>
                                Select WASM File to Download
                            </h3>
                            <button
                                onClick={onClose}
                                style={{
                                    backgroundColor: "transparent",
                                    border: "none",
                                    fontSize: "24px",
                                    cursor: "pointer",
                                    color: "#8B4513"
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{
                            padding: "20px",
                            flexGrow: 1,
                            overflow: "auto"
                        }}>
                            <p style={{
                                marginBottom: "16px",
                                color: "#5C4033",
                                fontSize: "14px"
                            }}>
                                Found {wasmFiles.length} WASM file{wasmFiles.length !== 1 ? 's' : ''} in your project.
                                Select which actor WASM you want to download:
                            </p>

                            <div style={{display: "flex", flexDirection: "column", gap: "12px"}}>
                                {wasmFiles.map((wasmFile, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            border: "1px solid #8B4513",
                                            borderRadius: "6px",
                                            padding: "16px",
                                            backgroundColor: "#fff",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center"
                                        }}
                                        onClick={() => onDownload(wasmFile)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "#f3e5d8";
                                            e.currentTarget.style.borderColor = "#5C4033";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "#fff";
                                            e.currentTarget.style.borderColor = "#8B4513";
                                        }}
                                    >
                                        <div>
                                            <div style={{
                                                fontWeight: "bold",
                                                color: "#5C4033",
                                                fontSize: "16px",
                                                marginBottom: "4px"
                                            }}>
                                                {wasmFile.actorName || wasmFile.fileName.replace('.wasm', '')}
                                            </div>
                                            <div style={{
                                                color: "#8B4513",
                                                fontSize: "13px",
                                                fontFamily: "monospace"
                                            }}>
                                                {wasmFile.path}/{wasmFile.fileName}
                                            </div>
                                            {wasmFile.size && (
                                                <div style={{
                                                    color: "#666",
                                                    fontSize: "12px",
                                                    marginTop: "4px"
                                                }}>
                                                    Size: {formatFileSize(wasmFile.size)}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{
                                            backgroundColor: "#5C4033",
                                            color: "#FFF5EE",
                                            padding: "6px 12px",
                                            borderRadius: "4px",
                                            fontSize: "12px",
                                            fontWeight: "500"
                                        }}>
                                            Download
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{
                            padding: "16px 20px",
                            borderTop: "1px solid #8B4513",
                            display: "flex",
                            justifyContent: "flex-end",
                            backgroundColor: "#f3e5d8"
                        }}>
                            <button
                                onClick={onClose}
                                style={{
                                    padding: "8px 16px",
                                    backgroundColor: "#8B4513",
                                    color: "#FFF5EE",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontWeight: "500"
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            );
        };


        return (
            <div style={{display: "flex", height: "100%", backgroundColor: "#fdf6e3"}}>
                {/* Sidebar */}
                <Sidebar
                    key={`sidebar-${project?.id || 'none'}`}
                    files={files}
                    motokoPackages={getMotokoSidebarPackages(storeProject?.motokoPackages || [])}
                    npmPackages={getNPMSidebarPackages(storeProject?.npmPackages || [])}
                    canisters={userCanisters}
                    onFileClick={setActiveFile}
                    activeFile={activeFile}
                    activeType={activeType}
                    onTypeChange={setActiveType}
                    isMotokoPackagesDialogOpen={isMotokoPackagesDialogOpen}
                    setIsMotokoPackagesDialogOpen={toggleMotokoPackagesDialog}
                    isNPMPackagesDialogOpen={isNPMPackagesDialogOpen}
                    setIsNPMPackagesDialogOpen={toggleNPMPackagesDialog}
                    onAddCanisterToProject={onAddCanisterToProject}
                    onRemoveCanisterFromProject={onRemoveCanisterFromProject}
                    onNewFile={showNewFile}
                    onNewFolder={showNewFolder}
                    onDeleteFile={deleteFile}
                    loading={loading || isDownloadingFiles}
                    project={storeProject || defaultProject}
                    systemCanisters={systemCanisters}
                    userPrincipal={userPrincipal}
                    frontendCanisterId={frontendCanisterId}
                    identity={identity}
                    assetRefreshTrigger={assetRefreshTrigger}
                    onFileSystemChange={async () => {
                        // Force refresh with cache bypass
                        await useIDEStore.getState().refreshFiles(undefined, true);
                    }}
                    onSuccess={onSuccess}
                    onError={onError}
                    refreshUserCanisters={async () => {
                        console.log('🔄 Refreshing user canisters...');
                        if (onProjectUpdate) {
                            await onProjectUpdate();
                        }
                    }}
                    refreshUserProjects={async () => {
                        console.log('🔄 Refreshing user projects...');
                        if (onProjectUpdate) {
                            await onProjectUpdate();
                        }
                    }}
                />

                {/* Main Editor Area */}
                <div className="ide-editor-container" style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0 // Important for flex child to shrink properly
                }}>
                    {/* Tab Navigation */}
                    <div style={{
                        display: "flex",
                        borderBottom: "1px solid #8B4513",
                        backgroundColor: "#f3e5d8",
                    }}>
                        <button
                            onClick={() => useIDEStore.getState().setActiveTab('editor')}
                            style={{
                                padding: "10px 16px",
                                fontSize: "14px",
                                fontWeight: activeTab === 'editor' ? "600" : "400",
                                backgroundColor: activeTab === 'editor' ? "#fdf6e3" : "#f3e5d8",
                                color: "#5C4033",
                                border: "none",
                                borderRight: "1px solid #8B4513",
                                borderBottom: activeTab === 'editor' ? "2px solid #5C4033" : "none",
                                marginBottom: activeTab === 'editor' ? "-1px" : "0",
                                cursor: "pointer"
                            }}
                        >
                            Editor
                        </button>
                        <button
                            onClick={() => useIDEStore.getState().setActiveTab('versions')}
                            style={{
                                padding: "10px 16px",
                                fontSize: "14px",
                                fontWeight: activeTab === 'versions' ? "600" : "400",
                                backgroundColor: activeTab === 'versions' ? "#fdf6e3" : "#f3e5d8",
                                color: "#5C4033",
                                border: "none",
                                borderRight: "1px solid #8B4513",
                                borderBottom: activeTab === 'versions' ? "2px solid #5C4033" : "none",
                                marginBottom: activeTab === 'versions' ? "-1px" : "0",
                                cursor: "pointer"
                            }}
                        >
                            Versions
                        </button>

                        {/* 🆕 NEW AI STUDIO TAB */}
                        <button
                            onClick={() => useIDEStore.getState().setActiveTab('ai-studio')}
                            style={{
                                padding: "10px 16px",
                                fontSize: "14px",
                                fontWeight: activeTab === 'ai-studio' ? "600" : "400",
                                backgroundColor: activeTab === 'ai-studio' ? "#fdf6e3" : "#f3e5d8",
                                color: "#5C4033",
                                border: "none",
                                borderRight: "1px solid #8B4513",
                                borderBottom: activeTab === 'ai-studio' ? "2px solid #5C4033" : "none",
                                marginBottom: activeTab === 'ai-studio' ? "-1px" : "0",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px"
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2">
                                <path
                                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                            </svg>
                            AI Studio
                        </button>
                    </div>

                    {/* Content Area */}
                    {activeTab === 'editor' ? (
                        // Editor Content
                        <div style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 0
                        }}>
                            {/* Editor Header */}
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "10px 15px",
                                backgroundColor: "#fdf6e3",
                                borderBottom: "1px solid #8B4513"
                            }}>
                                <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
                            <span style={{fontSize: "18px", fontWeight: "bold", color: "#5C4033"}}>
                                {activeFile ? activeFile.replace(/\/+/g, '/') : ''}
                            </span>
                                    {hasUnsavedChanges && (
                                        <span style={{
                                            fontSize: "14px",
                                            color: "#8B4513",
                                            backgroundColor: "#f3e5d8",
                                            padding: "2px 8px",
                                            borderRadius: "4px"
                                        }}>
                                    Unsaved changes
                                </span>
                                    )}
                                </div>
                                <div style={{display: 'flex', gap: '10px'}}>
                                    {/* Your existing buttons here */}
                                    {isDeployableFile(activeFile, fileContent) && (
                                        <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px"
                                        }}>
                                            <DeployButton
                                                onClick={(id, mode, projectType, selectedActor, selectedCanister) => {
                                                    console.log("🔥 Parent component received deploy request:");
                                                    console.log("  - id:", id);
                                                    console.log("  - mode:", mode);
                                                    console.log("  - projectType:", projectType);
                                                    console.log("  - selectedActor:", selectedActor);
                                                    console.log("  - selectedCanister:", selectedCanister);

                                                    // Store the canister mapping if provided
                                                    if (selectedActor && selectedCanister) {
                                                        const projectName = project?.name || 'unknown-project';
                                                        const getCanisterNameFromActor = (actorFile: string): string => {
                                                            const fileName = actorFile.split('/').pop() || actorFile;
                                                            const baseName = fileName.replace('.mo', '');
                                                            return baseName.toLowerCase() === 'main' ? 'main' : baseName.toLowerCase();
                                                        };
                                                        const canisterName = getCanisterNameFromActor(selectedActor);
                                                        const storageKey = `${projectName}:${canisterName}`;
                                                        localStorage.setItem(storageKey, selectedCanister);
                                                        console.log(`💾 Parent stored mapping: ${storageKey} = ${selectedCanister}`);
                                                    }

                                                    handleDeployWithClassCheck(id, mode, projectType, selectedCanister, selectedActor);
                                                }}
                                                disabled={compilationStatus !== 'idle' || (activeFile.endsWith('.mo') && !compilerReady)}
                                                compilationStatus={compilationStatus}
                                                deploymentError={deploymentError}
                                                useIDEStore={useIDEStore}
                                                activeType={activeType}
                                                files={files}
                                                userCanisters={userCanisters}
                                                setActiveFile={setActiveFile}
                                                project={project}
                                            />
                                            <button
                                                onClick={downloadWasm}
                                                style={{
                                                    backgroundColor: "#4B5320",
                                                    color: "#FFF5EE",
                                                    padding: "8px 16px",
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                    fontWeight: "500",
                                                    height: "32px"
                                                }}
                                            >
                                                Download WASM
                                            </button>
                                            <button
                                                onClick={handleDeleteWasmClick}
                                                style={{
                                                    backgroundColor: "#B22222",
                                                    color: "#FFF5EE",
                                                    padding: "8px 16px",
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                    fontWeight: "500",
                                                    height: "32px"
                                                }}
                                            >
                                                Delete WASM
                                            </button>
                                        </div>
                                    )}

                                    <button
                                        onClick={() => hasUnsavedChanges && useIDEStore.setState({showSaveConfirmation: true})}
                                        style={{
                                            backgroundColor: "#8B4513",
                                            color: "#FFF5EE",
                                            padding: "8px 16px",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: "500",
                                            height: "32px"
                                        }}
                                    >
                                        Save
                                    </button>

                                    {activeFile && activeFile.endsWith('.mo') && (
                                        <div style={{position: "relative", display: "inline-block"}}>
                                            <div
                                                onClick={() => handleEnableLogging()}
                                                style={{
                                                    width: "30px",
                                                    height: "30px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    backgroundColor: fileContent.includes('import L "mo:cn-logger/logger"')
                                                        ? "#e8f5e9" : "#f3e5d8",
                                                    border: `1px solid ${fileContent.includes('import L "mo:cn-logger/logger"')
                                                        ? "#4caf50" : "#8B4513"}`,
                                                    borderRadius: "8px",
                                                    cursor: "pointer",
                                                    transition: "all 0.2s ease",
                                                    fontSize: "16px"
                                                }}
                                                title={fileContent.includes('import L "mo:cn-logger/logger"')
                                                    ? "Logging is enabled - click to modify"
                                                    : "Click to enable logging"}
                                            >
                                                {fileContent.includes('import L "mo:cn-logger/logger"') ? "📈" : "📊"}
                                            </div>
                                            <div style={{
                                                position: "absolute",
                                                top: "-2px",
                                                right: "-2px",
                                                width: "8px",
                                                height: "8px",
                                                backgroundColor: fileContent.includes('import L "mo:cn-logger/logger"')
                                                    ? "#4CAF50" : "#FF9800",
                                                borderRadius: "50%",
                                                border: "1px solid white"
                                            }}/>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Error Messages */}
                            {deploymentError && (
                                <div style={{
                                    color: '#dc2626',
                                    fontSize: '15px',
                                    backgroundColor: '#fee2e2',
                                    padding: '12px 15px',
                                    margin: '4px',
                                    borderRadius: '6px',
                                    whiteSpace: "pre-wrap",
                                    position: "relative",
                                    border: '1px solid #fecaca'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: '12px'
                                    }}>
                                        <div style={{flex: 1}}>
                                            <div style={{
                                                fontWeight: '600',
                                                marginBottom: '6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                ⚠️ Deployment Error:
                                            </div>
                                            <div style={{
                                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                fontSize: '13px',
                                                fontFamily: 'monospace',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                maxHeight: '120px',
                                                overflowY: 'auto',
                                                border: '1px solid rgba(239, 68, 68, 0.2)'
                                            }}>
                                                {deploymentError}
                                            </div>
                                        </div>

                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '6px',
                                            alignItems: 'flex-end'
                                        }}>
                                            <button
                                                onClick={handleFixDeploymentError}
                                                style={{
                                                    padding: '8px 14px',
                                                    backgroundColor: '#dc2626',
                                                    color: '#FFF',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    whiteSpace: 'nowrap',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    transition: 'all 0.2s ease',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#b91c1c';
                                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#dc2626';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                }}
                                            >
                                                🔧 Fix This
                                            </button>

                                            <button
                                                onClick={() => useIDEStore.getState().setDeploymentError(null)}
                                                style={{
                                                    padding: '4px 8px',
                                                    background: 'transparent',
                                                    border: '1px solid #dc2626',
                                                    borderRadius: '4px',
                                                    fontSize: '11px',
                                                    color: '#dc2626',
                                                    cursor: 'pointer',
                                                    fontWeight: '500',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.1)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }}
                                            >
                                                × Dismiss
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Version Toolbar */}
                            <VersionToolbar
                                onSuccess={onSuccess}
                            />

                            {/* Editor and Chat Layout */}
                            <div style={{
                                flex: 1,
                                display: "flex",
                                minHeight: 0
                            }}>
                                {/* Editor Area */}
                                <div style={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    minWidth: 0
                                }}>
                                    {/* Monaco Editor */}
                                    <div style={{flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden'}}>
                                        <MonacoEditor
                                            height="100%"
                                            defaultLanguage="motoko"
                                            language={activeFile ? activeFile.split('.').pop() : 'motoko'}
                                            path={`file:///${activeFile}`}
                                            value={fileContent}
                                            onChange={handleEditorChange}
                                            onMount={handleEditorDidMount}
                                            theme="customTheme"
                                            options={{
                                                fontFamily: "'SF Mono', Monaco, Menlo, Consolas, 'Ubuntu Mono', monospace",
                                                fontSize: 14,
                                                lineHeight: 21,
                                                minimap: {enabled: false},
                                                scrollBeyondLastLine: false,
                                                renderLineHighlight: 'all',
                                                lineNumbers: 'on',
                                                guides: {indentation: true},
                                                suggestOnTriggerCharacters: true,
                                                quickSuggestions: true,
                                                folding: true,
                                                padding: {top: 12, bottom: 12},
                                                glyphMargin: true,
                                                hover: {
                                                    enabled: true,
                                                    delay: 300
                                                }
                                            }}
                                        />
                                    </div>

                                    {/* Tabbed Console */}
                                    <TabbedConsole
                                        activeFile={activeFile}
                                        fileContent={fileContent}
                                        onUpdateContent={handleClaudeContentUpdate}
                                        onError={onError}
                                        onSuccess={onSuccess}
                                        monaco={monaco}
                                        editorRef={editorRef}
                                        project={project}
                                        userCanisters={userCanisters}
                                        assetRefreshTrigger={assetRefreshTrigger}
                                        onAssetChange={() => setAssetRefreshTrigger(prev => prev + 1)}
                                    />
                                </div>

                                {/* Chat Sidebar */}
                                <ChatSidebar
                                    ref={chatSidebarRef}
                                    isExpanded={isChatExpanded}
                                    onToggle={() => setIsChatExpanded(!isChatExpanded)}
                                    activeFile={activeFile}
                                    fileContent={fileContent}
                                    files={files}
                                    project={project}
                                    onUpdateContent={handleClaudeContentUpdate}
                                    onError={onError}
                                    onSuccess={onSuccess}
                                    systemCanisters={systemCanisters}
                                    userPrincipal={userPrincipal}
                                    onFileSystemChange={() => {
                                        useIDEStore.getState().refreshFiles();
                                    }}
                                />
                            </div>
                        </div>
                    ) : activeTab === 'versions' ? (
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                            overflow: "hidden"
                        }}>
                            <div style={{
                                padding: "15px 20px",
                                backgroundColor: "#f3e5d8",
                                borderBottom: "1px solid #8B4513",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}>
                                <h2 style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                    color: "#5C4033",
                                    margin: 0
                                }}>
                                    Project Versions
                                </h2>
                                <button
                                    onClick={handleCreateVersionClick}
                                    style={{
                                        backgroundColor: "#5C4033",
                                        color: "#FFF5EE",
                                        padding: "8px 16px",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontWeight: "500"
                                    }}
                                >
                                    Create New Version
                                </button>
                            </div>

                            <div style={{flex: 1, overflow: "auto"}}>
                                <VersioningPanel
                                    projectId={project.id}
                                    canisterId={canisterId}
                                    userPrincipal={userPrincipal}
                                    userActor={systemCanisters.userActor}
                                    onVersionChange={switchToVersion}
                                    onVersionCreate={createNewVersion}
                                    onVersionStatusChange={updateVersionStatus}
                                    onError={onError}
                                    onSuccess={onSuccess}
                                    currentVersionId={currentVersionId}
                                    initializeProjectVersion={initializeProjectVersion}
                                    workingCopyBaseVersionId={project.workingCopyBaseVersion}
                                />
                            </div>
                        </div>
                    ) : activeTab === 'ai-studio' ? (
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                            overflow: "hidden"
                        }}>
                            <AIStudioView
                                project={project}
                                files={files}
                                systemCanisters={systemCanisters}
                                userCanisters={userCanisters}
                                userPrincipal={userPrincipal}
                                onError={onError}
                                onSuccess={onSuccess}
                                onProjectUpdate={onProjectUpdate}
                                onFileSystemChange={() => {
                                    useIDEStore.getState().refreshFiles();
                                }}
                                activeFile={activeFile || ''}
                                fileContent={fileContent || ''}
                                onUpdateContent={handleClaudeContentUpdate}
                            />
                        </div>
                    ) : null}


                </div>

                {/* All your existing dialogs remain the same */}
                <MotokoPackagesDialog
                    isOpen={isMotokoPackagesDialogOpen}
                    onClose={toggleMotokoPackagesDialog}
                    onAddPackage={addMotokoPackage}
                    onRemovePackage={removeMotokoPackage}
                    currentPackages={getPackages(project.motokoPackages)}
                />

                <NPMPackagesDialog
                    isOpen={isNPMPackagesDialogOpen}
                    onClose={toggleNPMPackagesDialog}
                    onAddPackage={addNPMPackage}
                    onRemovePackage={removeNPMPackage}
                    currentPackages={getNPMSidebarPackages(project.npmPackages)}
                />

                <CompilerErrorDialog
                    isOpen={showCompilerErrors}
                    onClose={() => useIDEStore.setState({showCompilerErrors: false})}
                    diagnostics={compilerDiagnostics}
                />

                {/* Enhanced Logging Preview Dialog with Usage Tips */}
                {showLoggingPreviewDialog && (
                    <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: "#fdf6e3",
                            borderRadius: "8px",
                            width: "85%",
                            maxWidth: "1000px",
                            maxHeight: "85vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            boxShadow: "0 4px 20px rgba(139, 69, 19, 0.3)"
                        }}>
                            <div style={{
                                padding: "16px 20px",
                                borderBottom: "1px solid #8B4513",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                backgroundColor: "#f3e5d8"
                            }}>
                                <h3 style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                    color: "#5C4033",
                                    margin: 0
                                }}>
                                    📊 Enable Logging - Preview Changes
                                </h3>
                                <button
                                    onClick={() => setShowLoggingPreviewDialog(false)}
                                    style={{
                                        backgroundColor: "transparent",
                                        border: "none",
                                        fontSize: "24px",
                                        cursor: "pointer",
                                        color: "#8B4513"
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            <div style={{
                                padding: "20px",
                                flexGrow: 1,
                                overflow: "auto"
                            }}>
                                <p style={{
                                    marginBottom: "16px",
                                    color: "#5C4033",
                                    fontSize: "14px"
                                }}>
                                    The following changes will be made to enable logging support:
                                </p>

                                <div style={{
                                    backgroundColor: "#f8f8f8",
                                    border: "1px solid #ddd",
                                    borderRadius: "6px",
                                    padding: "12px",
                                    fontFamily: "monospace",
                                    fontSize: "12px",
                                    maxHeight: "300px",
                                    overflow: "auto",
                                    whiteSpace: "pre-wrap",
                                    marginBottom: "24px"
                                }}>
                                    {previewCode}
                                </div>

                                <div style={{
                                    backgroundColor: "#e8f5e9",
                                    border: "1px solid #4caf50",
                                    borderRadius: "6px",
                                    padding: "16px",
                                    marginBottom: "16px"
                                }}>
                                    <h4 style={{
                                        color: "#2e7d32",
                                        margin: "0 0 12px 0",
                                        fontSize: "16px",
                                        fontWeight: "600",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px"
                                    }}>
                                        💡 How to Use Logging in Your Code
                                    </h4>

                                    <p style={{
                                        color: "#2e7d32",
                                        margin: "0 0 16px 0",
                                        fontSize: "14px",
                                        lineHeight: "1.5"
                                    }}>
                                        Once logging is enabled, you can add log statements anywhere in your functions. Here
                                        are examples:
                                    </p>

                                    <div style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: "16px"
                                    }}>
                                        <div>
                                            <h5 style={{
                                                color: "#1b5e20",
                                                margin: "0 0 8px 0",
                                                fontSize: "14px",
                                                fontWeight: "600"
                                            }}>
                                                Log Types:
                                            </h5>
                                            <div style={{
                                                backgroundColor: "#f1f8e9",
                                                padding: "12px",
                                                borderRadius: "4px",
                                                border: "1px solid #c8e6c9"
                                            }}>
                                                <code style={{
                                                    display: "block",
                                                    fontFamily: "monospace",
                                                    fontSize: "12px",
                                                    lineHeight: "1.6",
                                                    color: "#2e7d32"
                                                }}>
                                                    {`// For general information\nlogger.info("User authenticated successfully");\n\n// For warnings\nlogger.warn("Memory usage is high");\n\n// For errors\nlogger.error("Failed to save user data");\n\n// For debugging\nlogger.debug("Processing transaction #123");`}
                                                </code>
                                            </div>
                                        </div>

                                        <div>
                                            <h5 style={{
                                                color: "#1b5e20",
                                                margin: "0 0 8px 0",
                                                fontSize: "14px",
                                                fontWeight: "600"
                                            }}>
                                                Practical Examples:
                                            </h5>
                                            <div style={{
                                                backgroundColor: "#f1f8e9",
                                                padding: "12px",
                                                borderRadius: "4px",
                                                border: "1px solid #c8e6c9"
                                            }}>
                                                <code style={{
                                                    display: "block",
                                                    fontFamily: "monospace",
                                                    fontSize: "12px",
                                                    lineHeight: "1.6",
                                                    color: "#2e7d32"
                                                }}>
                                                    {`public func transfer(amount: Nat) : async () {\n    logger.info("Transfer initiated");\n    \n    if (amount > balance) {\n        logger.error("Insufficient balance");\n        return;\n    };\n    \n    // ... transfer logic\n    logger.info("Transfer completed");\n};`}
                                                </code>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{
                                        backgroundColor: "#fff3e0",
                                        border: "1px solid #ff9800",
                                        borderRadius: "4px",
                                        padding: "12px",
                                        marginTop: "16px"
                                    }}>
                                        <p style={{
                                            color: "#e65100",
                                            margin: 0,
                                            fontSize: "13px",
                                            fontWeight: "500"
                                        }}>
                                            <strong>💡 Pro Tip:</strong> After deploying, visit the "Logs & Monitoring" menu
                                            option to see your log outputs in real-time.
                                            You can filter by log level (INFO, WARN, ERROR, DEBUG) and search through your
                                            logs!
                                        </p>
                                    </div>
                                </div>

                                <div style={{
                                    backgroundColor: "#f3e5d8",
                                    border: "1px solid #8B4513",
                                    borderRadius: "6px",
                                    padding: "16px"
                                }}>
                                    <h4 style={{
                                        color: "#5C4033",
                                        margin: "0 0 12px 0",
                                        fontSize: "16px",
                                        fontWeight: "600",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px"
                                    }}>
                                        📋 Quick Reference
                                    </h4>

                                    <div style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(2, 1fr)",
                                        gap: "12px",
                                        fontSize: "13px"
                                    }}>
                                        <div style={{
                                            padding: "8px",
                                            backgroundColor: "#ffffff",
                                            borderRadius: "4px",
                                            border: "1px solid #d7c4a7"
                                        }}>
                                            <strong style={{color: "#2196F3"}}>ℹ️ INFO:</strong> General information,
                                            successful operations
                                        </div>
                                        <div style={{
                                            padding: "8px",
                                            backgroundColor: "#ffffff",
                                            borderRadius: "4px",
                                            border: "1px solid #d7c4a7"
                                        }}>
                                            <strong style={{color: "#FF9800"}}>⚠️ WARN:</strong> Potential issues,
                                            performance warnings
                                        </div>
                                        <div style={{
                                            padding: "8px",
                                            backgroundColor: "#ffffff",
                                            borderRadius: "4px",
                                            border: "1px solid #d7c4a7"
                                        }}>
                                            <strong style={{color: "#F44336"}}>❌ ERROR:</strong> Failures, exceptions,
                                            critical issues
                                        </div>
                                        <div style={{
                                            padding: "8px",
                                            backgroundColor: "#ffffff",
                                            borderRadius: "4px",
                                            border: "1px solid #d7c4a7"
                                        }}>
                                            <strong style={{color: "#9C27B0"}}>🔍 DEBUG:</strong> Detailed tracing,
                                            development info
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={{
                                padding: "16px 20px",
                                borderTop: "1px solid #8B4513",
                                display: "flex",
                                gap: "12px",
                                justifyContent: "flex-end",
                                backgroundColor: "#f3e5d8"
                            }}>
                                <button
                                    onClick={() => setShowLoggingPreviewDialog(false)}
                                    style={{
                                        padding: "10px 20px",
                                        backgroundColor: "#8B4513",
                                        color: "#FFF5EE",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontWeight: "500",
                                        fontSize: "14px"
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        const modifiedCode = injectLoggingSupport(fileContent, activeFile);
                                        handleEditorChange(modifiedCode);
                                        setShowLoggingPreviewDialog(false);
                                        onSuccess('🎉 Logging support enabled! Start adding logger.info(), logger.warn(), logger.error(), or logger.debug() to your functions.');
                                    }}
                                    style={{
                                        padding: "10px 20px",
                                        backgroundColor: "#4CAF50",
                                        color: "#ffffff",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontWeight: "500",
                                        fontSize: "14px",
                                        boxShadow: "0 2px 4px rgba(76, 175, 80, 0.3)"
                                    }}
                                >
                                    ✅ Enable Logging
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/* Version Management Dialog - Styled to match IDE theme */}
                {isVersioningDialogOpen && (
                    <div className="fixed inset-0 flex items-center justify-center z-50"
                         style={{
                             backgroundColor: 'rgba(0, 0, 0, 0.5)',
                             backdropFilter: 'blur(2px)'
                         }}>
                        <div style={{
                            backgroundColor: '#fdf6e3',
                            borderRadius: '8px',
                            width: '85%',
                            maxWidth: '1200px',
                            maxHeight: '85vh',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
                        }}>
                            <div style={{
                                padding: '16px 20px',
                                borderBottom: '1px solid #8B4513',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: '#f3e5d8'
                            }}>
                                <h3 style={{
                                    fontSize: '20px',
                                    fontWeight: 'bold',
                                    color: '#5C4033',
                                    margin: 0
                                }}>
                                    Version Management
                                </h3>
                                <button
                                    onClick={toggleVersioningDialog}
                                    style={{
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        fontSize: '24px',
                                        cursor: 'pointer',
                                        color: '#8B4513',
                                        width: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '4px'
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            <div style={{
                                flexGrow: 1,
                                overflow: 'auto',
                                padding: '0'
                            }}>
                                <VersioningPanel
                                    projectId={project.id}
                                    canisterId={canisterId}
                                    userPrincipal={userPrincipal}
                                    userActor={systemCanisters.userActor}
                                    onVersionChange={switchToVersion}
                                    onVersionCreate={createNewVersion}
                                    onVersionStatusChange={updateVersionStatus}
                                    onError={onError}
                                    onSuccess={onSuccess}
                                    currentVersionId={currentVersionId}
                                    initializeProjectVersion={initializeProjectVersion}
                                    workingCopyBaseVersionId={project.workingCopyBaseVersion}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* New File Dialog */}
                {
                    showNewFileDialog && (
                        <div style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1000
                        }}>
                            <div style={{
                                backgroundColor: "#fdf6e3",
                                padding: "20px",
                                borderRadius: "8px",
                                width: "400px",
                                boxShadow: "0px 4px 6px rgba(0,0,0,0.1)"
                            }}>
                                <h3 style={{
                                    margin: "0 0 15px 0",
                                    color: "#5C4033",
                                    fontSize: "18px",
                                    fontWeight: "500"
                                }}>
                                    Create New File
                                </h3>

                                <div style={{marginBottom: "15px"}}>
                                    <label style={{
                                        display: "block",
                                        marginBottom: "5px",
                                        color: "#5C4033",
                                        fontSize: "14px"
                                    }}>
                                        Directory Path:
                                    </label>

                                    {/* Checkbox for toggling between dropdown and custom input */}
                                    <div style={{marginBottom: "10px"}}>
                                        <label style={{fontSize: "14px", color: "#5C4033"}}>
                                            <input
                                                type="checkbox"
                                                checked={useCustomPath}
                                                onChange={() => useIDEStore.setState(state => ({
                                                    useCustomPath: !state.useCustomPath
                                                }))}
                                                style={{marginRight: "5px"}}
                                            />
                                            Create in custom directory path
                                        </label>
                                    </div>

                                    {useCustomPath ? (
                                        <input
                                            type="text"
                                            value={newFilePath || ''}
                                            onChange={(e) => useIDEStore.setState({newFilePath: e.target.value})}
                                            placeholder={`${project.name}/src/custom/path`}
                                            style={{
                                                width: "100%",
                                                padding: "8px",
                                                borderRadius: "4px",
                                                border: "1px solid #8B4513",
                                                backgroundColor: "#fff",
                                                fontSize: "14px",
                                                boxSizing: "border-box"
                                            }}
                                        />
                                    ) : (
                                        <select
                                            value={newFilePath ||
                                                (activeType === 'backend' ? `${project.name}/src/backend` : `${project.name}/src/frontend/src`)}
                                            onChange={(e) => useIDEStore.setState({newFilePath: e.target.value})}
                                            style={{
                                                width: "100%",
                                                padding: "8px",
                                                borderRadius: "4px",
                                                border: "1px solid #8B4513",
                                                backgroundColor: "#fff",
                                                fontSize: "14px",
                                                boxSizing: "border-box"
                                            }}
                                        >
                                            {generateDirectoryOptions(fileTree).map((dir, idx) => (
                                                <option key={idx} value={dir.path}>
                                                    {dir.path}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>

                                {/* Add the missing File Name field */}
                                <div style={{marginBottom: "15px"}}>
                                    <label style={{
                                        display: "block",
                                        marginBottom: "5px",
                                        color: "#5C4033",
                                        fontSize: "14px"
                                    }}>
                                        File Name:
                                    </label>
                                    <input
                                        type="text"
                                        value={newFileName}
                                        onChange={(e) => useIDEStore.setState({newFileName: e.target.value})}
                                        placeholder="filename.ext"
                                        style={{
                                            width: "100%",
                                            padding: "8px",
                                            borderRadius: "4px",
                                            border: "1px solid #8B4513",
                                            backgroundColor: "#fff",
                                            fontSize: "14px",
                                            boxSizing: "border-box"
                                        }}
                                    />
                                </div>

                                <div style={{display: "flex", gap: "10px", justifyContent: "flex-end"}}>
                                    <button
                                        onClick={() => useIDEStore.setState({
                                            showNewFileDialog: false,
                                            newFileName: '',
                                            newFilePath: null
                                        })}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#8B4513",
                                            color: "#FFF5EE",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: "500"
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => saveFile(true)}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#5C4033",
                                            color: "#FFF5EE",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: "500"
                                        }}
                                    >
                                        Create
                                    </button>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* New Folder Dialog */}
                {
                    showNewFolderDialog && (
                        <div style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1000
                        }}>
                            <div style={{
                                backgroundColor: "#fdf6e3",
                                padding: "20px",
                                borderRadius: "8px",
                                width: "400px",
                                boxShadow: "0px 4px 6px rgba(0,0,0,0.1)"
                            }}>
                                <h3 style={{
                                    margin: "0 0 15px 0",
                                    color: "#5C4033",
                                    fontSize: "18px",
                                    fontWeight: "500"
                                }}>
                                    Create New Folder
                                </h3>

                                <div style={{marginBottom: "15px"}}>
                                    <label style={{
                                        display: "block",
                                        marginBottom: "5px",
                                        color: "#5C4033",
                                        fontSize: "14px"
                                    }}>
                                        Parent Directory:
                                    </label>

                                    {/* Add a checkbox to toggle between dropdown and custom input */}
                                    <div style={{marginBottom: "10px"}}>
                                        <label style={{fontSize: "14px", color: "#5C4033"}}>
                                            <input
                                                type="checkbox"
                                                checked={useIDEStore(state => state.useCustomPath)}
                                                onChange={() => useIDEStore.setState(state => ({
                                                    useCustomPath: !state.useCustomPath
                                                }))}
                                                style={{marginRight: "5px"}}
                                            />
                                            Create in custom directory path
                                        </label>
                                    </div>

                                    {useIDEStore(state => state.useCustomPath) ? (
                                        <input
                                            type="text"
                                            value={newFilePath || ''}
                                            onChange={(e) => useIDEStore.setState({newFilePath: e.target.value})}
                                            placeholder={`${project.name}/src/custom/path`}
                                            style={{
                                                width: "100%",
                                                padding: "8px",
                                                borderRadius: "4px",
                                                border: "1px solid #8B4513",
                                                backgroundColor: "#fff",
                                                fontSize: "14px",
                                                boxSizing: "border-box"
                                            }}
                                        />
                                    ) : (
                                        <select
                                            value={newFilePath ||
                                                (activeType === 'backend' ? `${project.name}/src/backend` : `${project.name}/src/frontend/src`)}
                                            onChange={(e) => useIDEStore.setState({newFilePath: e.target.value})}
                                            style={{
                                                width: "100%",
                                                padding: "8px",
                                                borderRadius: "4px",
                                                border: "1px solid #8B4513",
                                                backgroundColor: "#fff",
                                                fontSize: "14px",
                                                boxSizing: "border-box"
                                            }}
                                        >
                                            {generateDirectoryOptions(fileTree).map((dir, idx) => (
                                                <option key={idx} value={dir.path}>
                                                    {dir.path}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>

                                <div style={{marginBottom: "15px"}}>
                                    <label style={{
                                        display: "block",
                                        marginBottom: "5px",
                                        color: "#5C4033",
                                        fontSize: "14px"
                                    }}>
                                        Folder Name:
                                    </label>
                                    <input
                                        type="text"
                                        value={newFileName}
                                        onChange={(e) => useIDEStore.setState({newFileName: e.target.value})}
                                        placeholder="folder-name"
                                        style={{
                                            width: "100%",
                                            padding: "8px",
                                            borderRadius: "4px",
                                            border: "1px solid #8B4513",
                                            backgroundColor: "#fff",
                                            fontSize: "14px",
                                            boxSizing: "border-box"
                                        }}
                                    />
                                </div>

                                <div style={{display: "flex", gap: "10px", justifyContent: "flex-end"}}>
                                    <button
                                        onClick={() => useIDEStore.setState({
                                            showNewFolderDialog: false,
                                            newFileName: '',
                                            newFilePath: null
                                            // Don't reset useCustomPath to preserve user preference
                                        })}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#8B4513",
                                            color: "#FFF5EE",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: "500"
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (newFileName) {
                                                const fullPath = newFilePath
                                                    ? pathUtils.joinPaths(newFilePath, newFileName)
                                                    : newFileName;
                                                createFolder(fullPath);
                                            }
                                        }}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#5C4033",
                                            color: "#FFF5EE",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: "500"
                                        }}
                                    >
                                        Create
                                    </button>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* Save Confirmation Dialog */
                }
                {
                    showSaveConfirmation && (
                        <div style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.25)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1000
                        }}>
                            <div style={{
                                backgroundColor: "#fdf6e3",
                                padding: "24px",
                                borderRadius: "8px",
                                width: "400px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                            }}>
                                <h3 style={{
                                    margin: "0 0 16px 0",
                                    color: "#5C4033",
                                    fontSize: "24px",
                                    fontWeight: "500"
                                }}>
                                    Save Changes?
                                </h3>
                                <p style={{
                                    margin: "0 0 24px 0",
                                    color: "#8B4513",
                                    fontSize: "16px"
                                }}>
                                    Do you want to save changes to {activeFile}?
                                </p>
                                <div style={{display: "flex", gap: "12px", justifyContent: "flex-end"}}>
                                    <button
                                        onClick={() => useIDEStore.setState({showSaveConfirmation: false})}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#8B4513",
                                            color: "#FFF5EE",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: "500",
                                            fontSize: "14px"
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => saveFile(false)}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#5C4033",
                                            color: "#FFF5EE",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: "500",
                                            fontSize: "14px"
                                        }}
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* Delete Confirmation Dialog */
                }
                {
                    showDeleteConfirmation && fileToDelete && (
                        <div style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.25)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1000
                        }}>
                            <div style={{
                                backgroundColor: "#fdf6e3",
                                padding: "24px",
                                borderRadius: "8px",
                                width: "450px",
                                maxWidth: "90%",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                            }}>
                                <h3 style={{
                                    margin: "0 0 16px 0",
                                    color: "#5C4033",
                                    fontSize: "24px",
                                    fontWeight: "500"
                                }}>
                                    Delete File?
                                </h3>
                                <p style={{
                                    margin: "0 0 24px 0",
                                    color: "#8B4513",
                                    fontSize: "16px",
                                    lineHeight: "1.5",
                                    wordBreak: "break-word"
                                }}>
                                    Are you sure you want to delete<br/>
                                    <strong style={{wordBreak: "break-all"}}>{fileToDelete}</strong>?<br/>
                                    This action cannot be undone.
                                </p>
                                <div style={{display: "flex", gap: "12px", justifyContent: "flex-end"}}>
                                    <button
                                        onClick={() => useIDEStore.setState({
                                            showDeleteConfirmation: false,
                                            fileToDelete: null
                                        })}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#8B4513",
                                            color: "#FFF5EE",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: "500",
                                            fontSize: "14px"
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmDeleteFile}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#DC2626",
                                            color: "#FFF5EE",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: "500",
                                            fontSize: "14px"
                                        }}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* WASM Not Found Dialog */
                }
                {
                    showWasmNotFoundDialog && (
                        <div style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.25)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1000
                        }}>
                            <div style={{
                                backgroundColor: "#fdf6e3",
                                padding: "24px",
                                borderRadius: "8px",
                                width: "400px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                            }}>
                                <h3 style={{
                                    margin: "0 0 16px 0",
                                    color: "#5C4033",
                                    fontSize: "24px",
                                    fontWeight: "500"
                                }}>
                                    WASM Not Found
                                </h3>
                                <p style={{
                                    margin: "0 0 24px 0",
                                    color: "#8B4513",
                                    fontSize: "16px"
                                }}>
                                    No WASM file found for this canister. Please deploy first to generate the WASM file.
                                </p>
                                <div style={{display: "flex", gap: "12px", justifyContent: "flex-end"}}>
                                    <button
                                        onClick={() => useIDEStore.setState({showWasmNotFoundDialog: false})}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#5C4033",
                                            color: "#FFF5EE",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: "500",
                                            fontSize: "14px"
                                        }}
                                    >
                                        OK
                                    </button>
                                </div>
                            </div>
                        </div>
                    )
                }
                <CreateVersionDialog
                    isOpen={isCreateVersionDialogOpen}
                    onClose={() => setIsCreateVersionDialogOpen(false)}
                    onCreateVersion={handleCreateVersion}
                    versions={projectVersions}
                />

                <ClassInitArgsDialog
                    isOpen={showClassInitArgsDialog}
                    onClose={() => {
                        setShowClassInitArgsDialog(false);
                        setPendingDeploymentId(null);
                        setPendingDeploymentMode(null);
                    }}
                    onSubmit={handleClassInitArgsSubmit}
                    actorClassName={actorClassInfo.className}
                    initParams={actorClassInfo.initParams}
                    deploymentMode={pendingDeploymentMode} // Pass the pending deployment mode
                />

                {/* WASM Selection Dialog */}
                <WasmSelectionDialog
                    isOpen={showWasmSelectionDialog}
                    wasmFiles={availableWasmFiles}
                    onDownload={downloadSelectedWasm}
                    onClose={hideWasmSelection}
                />

                {/* Oversized WASM Warning Dialog */}
                {showOversizedWasmWarning && (
                    <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: "#fdf6e3",
                            borderRadius: "8px",
                            width: "500px",
                            maxWidth: "90%",
                            padding: "20px",
                            boxShadow: "0 4px 20px rgba(139, 69, 19, 0.3)"
                        }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                marginBottom: "15px"
                            }}>
                                <span style={{fontSize: "24px"}}>⚠️</span>
                                <h3 style={{color: "#B22222", margin: 0, fontSize: "18px"}}>
                                    Large WASM Files Detected
                                </h3>
                            </div>

                            <p style={{color: "#5C4033", marginBottom: "15px"}}>
                                The following WASM files exceed the 2MB storage limit and may cause deployment issues:
                            </p>

                            <ul style={{color: "#8B4513", marginBottom: "20px"}}>
                                {oversizedWasmFiles.map((file, index) => (
                                    <li key={index} style={{marginBottom: "5px"}}>{file}</li>
                                ))}
                            </ul>

                            <div style={{display: "flex", gap: "10px", justifyContent: "flex-end"}}>
                                <button
                                    onClick={() => useIDEStore.setState({showOversizedWasmWarning: false})}
                                    style={{
                                        padding: "8px 16px",
                                        backgroundColor: "#8B4513",
                                        color: "#FFF5EE",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer"
                                    }}
                                >
                                    Dismiss
                                </button>
                                <button
                                    onClick={() => {
                                        useIDEStore.setState({showOversizedWasmWarning: false});
                                        handleDeleteWasmClick();
                                    }}
                                    style={{
                                        padding: "8px 16px",
                                        backgroundColor: "#B22222",
                                        color: "#FFF5EE",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer"
                                    }}
                                >
                                    Delete Files
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* WASM Deletion Selection Dialog */}
                {showWasmDeletionDialog && (
                    <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: "#fdf6e3",
                            borderRadius: "8px",
                            width: "600px",
                            maxWidth: "90%",
                            maxHeight: "80vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            boxShadow: "0 4px 20px rgba(139, 69, 19, 0.3)"
                        }}>
                            <div style={{
                                padding: "16px 20px",
                                borderBottom: "1px solid #8B4513",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                backgroundColor: "#f3e5d8"
                            }}>
                                <h3 style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                    color: "#5C4033",
                                    margin: 0
                                }}>
                                    Select WASM File to Delete
                                </h3>
                                <button
                                    onClick={() => useIDEStore.setState({
                                        showWasmDeletionDialog: false,
                                        availableWasmFilesForDeletion: []
                                    })}
                                    style={{
                                        backgroundColor: "transparent",
                                        border: "none",
                                        fontSize: "24px",
                                        cursor: "pointer",
                                        color: "#8B4513"
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            <div style={{
                                padding: "20px",
                                flexGrow: 1,
                                overflow: "auto"
                            }}>
                                <p style={{
                                    marginBottom: "16px",
                                    color: "#5C4033",
                                    fontSize: "14px"
                                }}>
                                    Found {availableWasmFilesForDeletion.length} WASM
                                    file{availableWasmFilesForDeletion.length !== 1 ? 's' : ''} in project artifacts:
                                </p>

                                <div style={{display: "flex", flexDirection: "column", gap: "12px"}}>
                                    {availableWasmFilesForDeletion.map((wasmFile, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                border: "1px solid #8B4513",
                                                borderRadius: "6px",
                                                padding: "16px",
                                                backgroundColor: "#fff",
                                                cursor: "pointer",
                                                transition: "all 0.2s ease",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center"
                                            }}
                                            onClick={() => handleDeleteSelectedWasm(wasmFile)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = "#f3e5d8";
                                                e.currentTarget.style.borderColor = "#5C4033";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = "#fff";
                                                e.currentTarget.style.borderColor = "#8B4513";
                                            }}
                                        >
                                            <div>
                                                <div style={{
                                                    fontWeight: "bold",
                                                    color: "#5C4033",
                                                    fontSize: "16px",
                                                    marginBottom: "4px"
                                                }}>
                                                    {wasmFile.fileName}
                                                </div>
                                                <div style={{
                                                    color: "#8B4513",
                                                    fontSize: "13px",
                                                    fontFamily: "monospace"
                                                }}>
                                                    {wasmFile.fullPath}
                                                </div>
                                                {wasmFile.size && (
                                                    <div style={{
                                                        color: wasmFile.size > 2 * 1024 * 1024 ? "#B22222" : "#666",
                                                        fontSize: "12px",
                                                        marginTop: "4px"
                                                    }}>
                                                        Size: {(wasmFile.size / (1024 * 1024)).toFixed(2)} MB
                                                        {wasmFile.size > 2 * 1024 * 1024 && " ⚠️ Over limit"}
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{
                                                backgroundColor: "#B22222",
                                                color: "#FFF5EE",
                                                padding: "6px 12px",
                                                borderRadius: "4px",
                                                fontSize: "12px",
                                                fontWeight: "500"
                                            }}>
                                                Delete
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Payload Size Error Dialog */}
                {showPayloadSizeErrorDialog && (
                    <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: "#fdf6e3",
                            borderRadius: "8px",
                            width: "500px",
                            maxWidth: "90%",
                            padding: "20px",
                            boxShadow: "0 4px 20px rgba(139, 69, 19, 0.3)"
                        }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                marginBottom: "15px"
                            }}>
                                <span style={{fontSize: "24px"}}>⚠️</span>
                                <h3 style={{color: "#B22222", margin: 0, fontSize: "18px"}}>
                                    Project Files Too Large
                                </h3>
                            </div>

                            <p style={{color: "#5C4033", marginBottom: "15px"}}>
                                Your project contains files that exceed the 2MB limit, preventing normal operations.
                                This is typically caused by large WASM files from previous deployments.
                            </p>

                            <div style={{
                                backgroundColor: "#fff3e0",
                                border: "1px solid #ff9800",
                                borderRadius: "4px",
                                padding: "12px",
                                marginBottom: "20px",
                                fontSize: "13px"
                            }}>
                                <strong>Solution:</strong> Delete the large WASM files to restore normal project
                                functionality.
                                You can redeploy later to generate fresh, smaller WASM files.
                            </div>

                            <div style={{display: "flex", gap: "10px", justifyContent: "flex-end"}}>
                                <button
                                    onClick={() => {
                                        useIDEStore.setState({
                                            showPayloadSizeErrorDialog: false,
                                            payloadSizeErrorMessage: null
                                        });
                                    }}
                                    style={{
                                        padding: "8px 16px",
                                        backgroundColor: "#8B4513",
                                        color: "#FFF5EE",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer"
                                    }}
                                >
                                    Dismiss
                                </button>
                                <button
                                    onClick={() => {
                                        // Close this dialog and open the WASM deletion dialog
                                        useIDEStore.setState({
                                            showPayloadSizeErrorDialog: false,
                                            payloadSizeErrorMessage: null
                                        });
                                        // Trigger the same logic as your existing Delete WASM button
                                        handleDeleteWasmClick();
                                    }}
                                    style={{
                                        padding: "8px 16px",
                                        backgroundColor: "#B22222",
                                        color: "#FFF5EE",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontWeight: "500"
                                    }}
                                >
                                    Delete Large Files
                                </button>
                            </div>
                        </div>
                    </div>
                )}


            </div>
        )
            ;
    }
;

export default MonacoIDEWithZustand;